import { toFullState } from '@/utilities/address';
import { COMMON_ACCESSORIALS, SERVICE_ACCESSORIALS, SERVICE_LEVELS } from '@/components/Account/Tariffs/constants';
import {
    ACCESSORIALS as DEFAULT_ACCESSORIALS,
    RATES as DEFAULT_RATES,
} from '@/components/Account/Tariffs/types/default';
import {
    ACCESSORIALS as LIVING_SPACES_ACCESSORIALS,
    RATES as LIVING_SPACES_RATES,
} from '@/components/Account/Tariffs/types/livingSpaces';
import {
    ACCESSORIALS as AMERICAN_FREIGHT_ACCESSORIALS,
    RATES as AMERICAN_FREIGHT_RATES,
} from '@/components/Account/Tariffs/types/americanFreight';
import {
    ACCESSORIALS as WERNER_ACCESSORIALS,
    RATES as WERNER_RATES,
} from '@/components/Account/Tariffs/types/wernerDefault';
import { RATES as DRIVER_DAILY_FLAT_RATES } from '@/components/Account/Tariffs/driver/dailyFlat';
import { RATES as DRIVER_DAILY_MILEAGE_RATES } from '@/components/Account/Tariffs/driver/dailyMileage';
import { ALL_RATES, RATE_CATEGORIES, RATE_UNITS } from './constants';

/**
 * The shipper's billing name, address, and email for the Billing Info section. The name falls back
 * to the business name; address and email are null when not set.
 * @param {Object} shipper the partnership's `shipper` (a `clients` row)
 * @returns {{ name: string, address: string | null, email: string | null }}
 */
export const getBillingInfo = (shipper) => ({
    name: shipper?.billing_name || shipper?.business_name || 'Unnamed shipper',
    address: shipper?.billing_address
        ? [
              shipper.billing_address,
              shipper.billing_address_unit,
              shipper.billing_city,
              toFullState(shipper.billing_state),
              shipper.billing_zip,
          ]
              .filter(Boolean)
              .join(', ')
        : null,
    email: shipper?.billing_email || null,
});

const tariffName = (tariff) => tariff?.name || 'Unnamed tariff';

const DEFAULT_DRIVERS_LABEL = 'All drivers (default)';

/** "FINAL_MILE" → "Final Mile". */
const titleCase = (value) =>
    value
        ? value
              .toLowerCase()
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
        : null;

/**
 * Order tariffs for display, one per override on the carrier's side of the pairing.
 * @param {Object[]} overrides `pricing_overrides` rows with `override_type` and `tariff { ... rates }`
 * @returns {{ key: string, name: string, algoType: string, leg: string | null, fuelSurcharge: boolean, rates: Object[] }[]}
 */
export const toOrderTariffs = (overrides = []) =>
    overrides.flatMap((override) =>
        override?.tariff
            ? [
                  {
                      key: override.pricing_override_id,
                      name: tariffName(override.tariff),
                      algoType: override.tariff.algo_type,
                      leg: titleCase(override.override_type),
                      fuelSurcharge: !!override.add_fuel_surcharge,
                      rates: override.tariff.rates || [],
                  },
              ]
            : []
    );

/**
 * Driver tariffs for display: the all-drivers default first, then the rest by driver name.
 * @param {Object[]} mappings `driver_tariff_mappings` rows with `tariff { ... rates }`
 * @param {Object[]} teammates the carrier's teammates (`teammate_id`, `username`)
 * @returns {{ key: string, name: string, algoType: string, appliesTo: string, rates: Object[] }[]}
 */
export const toDriverTariffs = (mappings = [], teammates = []) => {
    const names = Object.fromEntries(teammates.map((tm) => [tm.teammate_id, tm.username]));
    return mappings
        .flatMap((mapping) =>
            mapping?.tariff
                ? [
                      {
                          key: mapping.mapping_id,
                          name: tariffName(mapping.tariff),
                          algoType: mapping.tariff.algo_type,
                          appliesTo: mapping.driver_id
                              ? names[mapping.driver_id] || 'Unknown driver'
                              : DEFAULT_DRIVERS_LABEL,
                          rates: mapping.tariff.rates || [],
                      },
                  ]
                : []
        )
        .sort((a, b) => {
            const aDefault = a.appliesTo === DEFAULT_DRIVERS_LABEL;
            const bDefault = b.appliesTo === DEFAULT_DRIVERS_LABEL;
            if (aDefault !== bDefault) return aDefault ? -1 : 1;
            return a.appliesTo.localeCompare(b.appliesTo);
        });
};

/**
 * Every subregion the tariffs' rates point at, for looking up their names.
 * @param {{ rates: Object[] }[]} tariffs from `toOrderTariffs` / `toDriverTariffs`
 * @returns {string[]} unique `subregion_id`s, sorted
 */
export const getSubregionIds = (tariffs = []) =>
    [
        ...new Set(
            tariffs.flatMap((tariff) =>
                (tariff.rates || []).flatMap((rate) => (rate?.subregion_id ? [rate.subregion_id] : []))
            )
        ),
    ].sort();

const isOpenEnded = (value) => value === null || value === undefined || value === '' || !Number.isFinite(Number(value));

// Up to 6 decimals: the locale default (3) would round small rates such as 0.0525.
const formatNumber = (value) =>
    value === null || value === undefined || value === ''
        ? null
        : Number(value).toLocaleString('en-US', { maximumFractionDigits: 6 });

/** One axis's band ("0 – 25", "75+", "Up to 10"), or null when it covers everything. */
const formatBand = (minValue, maxValue) => {
    const min = isOpenEnded(minValue) ? null : Number(minValue);
    const max = isOpenEnded(maxValue) ? null : formatNumber(maxValue);
    if (min !== null && max !== null) return `${formatNumber(min)} – ${max}`;
    if (min) return `${formatNumber(min)}+`;
    if (max !== null) return `Up to ${max}`;
    return null;
};

const formatAmount = (value, unit) => {
    if (value === null || value === undefined || value === '') return '--';
    const number = Number(value);
    if (unit === RATE_UNITS.PERCENT) return `${formatNumber(number * 100)}%`;
    if (unit === RATE_UNITS.DOLLAR) {
        const dollars = Math.abs(number).toLocaleString('en-US', {
            minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
            maximumFractionDigits: 6,
        });
        return `${number < 0 ? '-' : ''}$${dollars}`;
    }
    return formatNumber(number);
};

/**
 * The range joins every axis that tells rows apart: the subregion, the first band, and the second
 * band (`min2` / `max2`), e.g. "Downtown · 0 – 25 · 10+". A rate that covers everything reads "Any".
 */
const formatRateRow = (rate, { unit, subregionName }) => {
    const parts = [subregionName, formatBand(rate?.min, rate?.max), formatBand(rate?.min2, rate?.max2)].filter(Boolean);
    return {
        range: parts.length ? parts.join(' · ') : 'Any',
        rate: formatAmount(rate?.rate, unit),
    };
};

const unitOf = (definition) => {
    if (definition?.isPercentage) return RATE_UNITS.PERCENT;
    return definition?.adornment === '' ? RATE_UNITS.PLAIN : RATE_UNITS.DOLLAR;
};

/** Each legacy algorithm's editor grids (`name` + the rate types in its `buckets`) and accessorials. */
const LEGACY_LAYOUTS = {
    DEFAULT: { sections: DEFAULT_RATES, accessorials: DEFAULT_ACCESSORIALS },
    SHIPPER_GLOBAL_DEFAULT: { sections: DEFAULT_RATES, accessorials: DEFAULT_ACCESSORIALS },
    CARRIER_GLOBAL_DEFAULT: { sections: DEFAULT_RATES, accessorials: DEFAULT_ACCESSORIALS },
    LIVING_SPACES: { sections: LIVING_SPACES_RATES, accessorials: LIVING_SPACES_ACCESSORIALS },
    ROCKET_SHIPPING: { sections: AMERICAN_FREIGHT_RATES, accessorials: AMERICAN_FREIGHT_ACCESSORIALS },
    WERNER_CARRIER_DEFAULT: { sections: WERNER_RATES, accessorials: WERNER_ACCESSORIALS },
    DRIVER_DAILY_FLAT: { sections: DRIVER_DAILY_FLAT_RATES, accessorials: [] },
    DRIVER_DAILY_MILEAGE: { sections: DRIVER_DAILY_MILEAGE_RATES, accessorials: [] },
};

/** Onward v2 per-service-level rates the editor shows outside the accessorial list. */
const SERVICE_LEVEL_RATE_LABELS = {
    BASE_RATE: 'Base Rate',
    MIN_RATE: 'Minimum',
    ITEMS_INCLUDED: 'Items Included',
    ITEMS_TOTAL_RATE: 'Total Items',
    ITEM_RATE: 'Rate Class',
};

const SERVICE_LEVEL_SUFFIXES = [
    ...Object.keys(SERVICE_LEVEL_RATE_LABELS),
    ...SERVICE_ACCESSORIALS.map((a) => a.type),
    'ADDITIONAL_PRIMARY_RATE',
    'ADDITIONAL_SECONDARY_RATE',
];

/** Fuel types that aren't dollars: the surcharge is a percentage, MPG is a plain number. */
const FUEL_UNITS = { FUEL_PRICE_MODIFIER: RATE_UNITS.PERCENT, FUEL_PRICE_MPG: RATE_UNITS.PLAIN };

/** Onward v2 per-service-level rates that are counts, not dollars. */
const SERVICE_LEVEL_PLAIN_SUFFIXES = ['ITEMS_INCLUDED'];

const FUEL_LABELS = {
    FUEL_PRICE_FLAT: 'Flat FSC per order',
    FUEL_PRICE_MPG: 'Vehicle MPG',
    FUEL_PRICE_MODIFIER: 'Fuel Surcharge',
    FUEL_PRICE_BY_ITEM: 'Fuel per item',
    FUEL_PRICE_BY_MILE: 'Fuel per mile',
    FUEL_PRICE_BY_CABINET: 'Fuel per cabinet',
};

const FUEL_TYPES = Object.keys(FUEL_LABELS);

const CUSTOM_SERVICE_LEVEL = 'CUSTOM_SERVICE_LEVEL';
const CUSTOM_ACCESSORIAL_PREFIX = 'CUSTOM_ACCESSORIAL_';

/** Longest first, so `CURBSIDE_NO_CONTACT_2MAN_BASE_RATE` doesn't match `CURBSIDE`. */
const SERVICE_LEVEL_PREFIXES = [CUSTOM_SERVICE_LEVEL, ...Object.keys(SERVICE_LEVELS)].sort(
    (a, b) => b.length - a.length
);

const COMMON_ACCESSORIAL_TYPES = COMMON_ACCESSORIALS.map((accessorial) => accessorial.type);

const ONWARD_DEFINITIONS = Object.fromEntries(
    [...SERVICE_ACCESSORIALS, ...COMMON_ACCESSORIALS].map((accessorial) => [accessorial.type, accessorial])
);

const ONWARD_LABELS = Object.fromEntries(
    Object.values(ONWARD_DEFINITIONS).map((accessorial) => [accessorial.type, accessorial.label])
);

/** "MILES_CURBSIDE_BASE_RATE" → "Miles Curbside Base Rate", for types no editor names. */
const humanize = (type) => titleCase(type) || 'Unnamed rate';

/** Fixed categories sort after every service level and grid section. */
const FIXED_ORDER_BASE = 1000;

/**
 * A rate in one of `RATE_CATEGORIES`. `rank` orders it within the category (lower first); `unit`
 * is how its amount reads.
 */
const inFixed = ({ category, name, rank = Infinity, unit = RATE_UNITS.DOLLAR }) => ({
    category: { ...category, order: FIXED_ORDER_BASE + Object.values(RATE_CATEGORIES).indexOf(category) },
    name,
    rank,
    unit,
});

const rankOf = (list, value) => {
    const index = list.indexOf(value);
    return index >= 0 ? index : Infinity;
};

const splitServiceLevelType = (type) => {
    const prefix = SERVICE_LEVEL_PREFIXES.find((p) => type.startsWith(`${p}_`));
    if (!prefix) return null;
    const suffix = type.slice(prefix.length + 1);
    return SERVICE_LEVEL_SUFFIXES.includes(suffix) ? { prefix, suffix } : null;
};

const categorizeLegacyRate = (type, { sections, accessorials }) => {
    const sectionIndex = sections.findIndex((section) =>
        section.buckets.some((row) => row.some((cell) => cell.type === type))
    );
    if (sectionIndex >= 0) {
        const { name } = sections[sectionIndex];
        return {
            category: { key: `SECTION_${name}`, label: name, order: sectionIndex },
            name: humanize(type),
            rank: 0,
            unit: RATE_UNITS.DOLLAR,
        };
    }
    const accessorialIndex = accessorials.findIndex((a) => a.type === type);
    if (accessorialIndex >= 0) {
        const accessorial = accessorials[accessorialIndex];
        return inFixed({
            category: RATE_CATEGORIES.ACCESSORIALS,
            name: accessorial.label,
            rank: accessorialIndex,
            unit: unitOf(accessorial),
        });
    }
    return inFixed({ category: RATE_CATEGORIES.OTHER, name: humanize(type), unit: RATE_UNITS.PLAIN });
};

const categorizeOnwardRate = (rate, type, serviceLevelNames) => {
    if (type === 'SERVICE_RATE') {
        return inFixed({ category: RATE_CATEGORIES.SERVICES, name: rate.dimensions?.code || 'Service' });
    }

    const serviceLevel = splitServiceLevelType(type);
    if (serviceLevel) {
        const { prefix, suffix } = serviceLevel;
        const label = SERVICE_LEVEL_RATE_LABELS[suffix] || ONWARD_LABELS[suffix];
        const name =
            suffix === 'ITEM_RATE' && rate.dimensions?.rate_class ? `${label}: ${rate.dimensions.rate_class}` : label;
        const rank = rankOf(SERVICE_LEVEL_SUFFIXES, suffix);
        const unit = SERVICE_LEVEL_PLAIN_SUFFIXES.includes(suffix)
            ? RATE_UNITS.PLAIN
            : unitOf(ONWARD_DEFINITIONS[suffix]);
        if (prefix === CUSTOM_SERVICE_LEVEL) {
            const id = rate.service_level_id;
            const category = {
                key: `${CUSTOM_SERVICE_LEVEL}_${id}`,
                label: serviceLevelNames[id] || 'Custom service level',
                order: 0,
            };
            return { category, name, rank, unit };
        }
        const order = 1 + Object.keys(SERVICE_LEVELS).indexOf(prefix);
        return { category: { key: prefix, label: SERVICE_LEVELS[prefix], order }, name, rank, unit };
    }

    const definition = ONWARD_DEFINITIONS[type];
    if (definition) {
        return inFixed({
            category: RATE_CATEGORIES.ACCESSORIALS,
            name: definition.label,
            rank: rankOf(COMMON_ACCESSORIAL_TYPES, type),
            unit: unitOf(definition),
        });
    }
    return inFixed({ category: RATE_CATEGORIES.OTHER, name: humanize(type), unit: RATE_UNITS.PLAIN });
};

/**
 * Which editor section a rate belongs to, what the editor calls it, its place in that section, and
 * how its amount reads (`RATE_UNITS`).
 * Category `order` follows the editor: service levels (custom first) or grid sections, then the fixed categories.
 */
const categorizeRate = (rate, { algoType, serviceLevelNames }) => {
    const type = rate?.type || '';
    if (type.startsWith('FUEL_')) {
        return inFixed({
            category: RATE_CATEGORIES.FUEL,
            name: FUEL_LABELS[type] || humanize(type),
            rank: rankOf(FUEL_TYPES, type),
            unit: FUEL_UNITS[type] || RATE_UNITS.DOLLAR,
        });
    }
    if (type.startsWith(CUSTOM_ACCESSORIAL_PREFIX)) {
        return inFixed({
            category: RATE_CATEGORIES.CUSTOM_ACCESSORIALS,
            name: type.slice(CUSTOM_ACCESSORIAL_PREFIX.length),
        });
    }
    const legacy = LEGACY_LAYOUTS[algoType];
    return legacy ? categorizeLegacyRate(type, legacy) : categorizeOnwardRate(rate, type, serviceLevelNames);
};

const toSortNumber = (value) => {
    const number = Number(value);
    return Number.isNaN(number) ? Infinity : number;
};

/** Ascending, with `Infinity` equal to itself (plain subtraction gives NaN). */
const compareNumbers = (a, b) => (a === b ? 0 : a < b ? -1 : 1);

/**
 * A tariff's rates as display rows, grouped, named, ordered and in the units the tariff editor uses.
 * @param {Object[]} rates the tariff's `rates` rows
 * @param {{ algoType?: string, serviceLevels?: { service_level_id: string, service_level: string }[],
 *   subregionNames?: Object<string, string> }} options the tariff's `algo_type`, the carrier's own
 *   service levels (names for custom service level rates), and subregion names by `subregion_id`
 * @returns {{ key: string, category: { key: string, label: string, order: number }, name: string, range: string, rate: string }[]}
 */
export const describeRates = (rates = [], { algoType, serviceLevels = [], subregionNames = {} } = {}) => {
    const serviceLevelNames = Object.fromEntries(
        serviceLevels.map((level) => [level.service_level_id, level.service_level])
    );
    return rates
        .map((rate, index) => {
            const { category, name, rank, unit } = categorizeRate(rate, { algoType, serviceLevelNames });
            const subregionName = rate?.subregion_id ? subregionNames[rate.subregion_id] || 'Unknown area' : undefined;
            const { range, rate: amount } = formatRateRow(rate, { unit, subregionName });
            return {
                row: {
                    key: `${rate?.type}-${rate?.service_level_id}-${rate?.subregion_id}-${rate?.min}-${rate?.min2}-${index}`,
                    category,
                    name: rate?.label || name,
                    range,
                    rate: amount,
                },
                sortKeys: [rank, subregionName || '', toSortNumber(rate?.min), toSortNumber(rate?.min2)],
            };
        })
        .sort(
            (a, b) =>
                a.row.category.order - b.row.category.order ||
                a.row.category.label.localeCompare(b.row.category.label) ||
                compareNumbers(a.sortKeys[0], b.sortKeys[0]) ||
                a.row.name.localeCompare(b.row.name) ||
                a.sortKeys[1].localeCompare(b.sortKeys[1]) ||
                compareNumbers(a.sortKeys[2], b.sortKeys[2]) ||
                compareNumbers(a.sortKeys[3], b.sortKeys[3])
        )
        .map(({ row }) => row);
};

/**
 * The rate filter's options: "All rates", then each category the rows use, in row order.
 * @param {{ category: { key: string, label: string } }[]} rows from `describeRates`
 * @returns {{ key: string, label: string }[]}
 */
export const getRateCategories = (rows = []) => {
    const seen = new Map();
    rows.forEach(({ category }) => {
        if (!seen.has(category.key)) seen.set(category.key, { key: category.key, label: category.label });
    });
    return [ALL_RATES, ...seen.values()];
};

/**
 * Rows in the chosen category whose name, category, or range matches the search.
 * @param {Object[]} rows from `describeRates`
 * @param {{ category?: string, search?: string }} filters a category key (`ALL_RATES.key` for all) and search text
 * @returns {Object[]}
 */
export const filterRates = (rows = [], { category = ALL_RATES.key, search = '' } = {}) => {
    const needle = search.trim().toLowerCase();
    return rows.filter(
        (row) =>
            (category === ALL_RATES.key || row.category.key === category) &&
            (!needle || `${row.name} ${row.category.label} ${row.range}`.toLowerCase().includes(needle))
    );
};
